import { useCallback, useRef, useState } from 'react'
import type { NodeEditToolbarRef } from '@/components/app/editor/tools/node-edit-toolbar'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Node edit mode (Edit Path) state: the shape being path-edited, its snapshots for undo/reset, the
 * toolbar handle, the undo floor, and the pen-edit sub-mode's hover. `exitNodeEditMode` is the one
 * exit every other mode and command goes through.
 */
export function useNodeEditState({
  isPenToolActive,
  onNodeEditModeChange,
}: {
  isPenToolActive: boolean
  onNodeEditModeChange: CenterCanvasConfig['onNodeEditModeChange']
}) {
  const [nodeEditShapeId, setNodeEditShapeId] = useState<string | null>(null)
  const [nodeEditShapeData, setNodeEditShapeData] = useState<ShapeData | null>(null)
  // Ref so the resize callback can read the current editing ID without a dep-array entry
  const nodeEditShapeIdRef = useRef<string | null>(null)
  // Captures the shape state BEFORE the first live path-change in each drag cycle so
  // handleNodeEditPathCommit can build a correct undo "before" snapshot.
  const nodeEditPreDragShapeRef = useRef<ShapeData | null>(null)
  // Captures the shape state at the moment node-edit mode was entered so Reset can restore it.
  const nodeEditOriginalShapeRef = useRef<ShapeData | null>(null)
  // Imperative handle onto NodeEditToolbar so Escape can ask it to cancel an in-progress
  // grip-handle drag instead of also exiting node-edit mode (see the node-edit-mode
  // keydown-capture effect in `use-edit-mode-key-capture`).
  const nodeEditToolbarRef = useRef<NodeEditToolbarRef>(null)
  // Shared undo stack's depth as of the moment node-edit mode was entered — `isNodeEditUndoAtFloor`
  // (the imperative handle in `use-center-canvas-handle`) tells `handleUndo` to exit the mode
  // instead of undoing past this, mirroring `cropUndoFloorRef`. Re-captured on every
  // `nodeEditShapeId` transition (`use-node-edit-session`).
  const nodeEditUndoFloorRef = useRef<number | null>(null)

  nodeEditShapeIdRef.current = nodeEditShapeId
  const isNodeEditMode = nodeEditShapeId != null
  // Pen-edit sub-mode: pen tool active while in node-edit mode
  const isPenEditMode = isNodeEditMode && isPenToolActive

  // Tracks which element the pen-edit hover is over so canvasCursor can reflect it
  const [penEditHoverKind, setPenEditHoverKind] = useState<
    'endpoint' | 'anchor' | 'segment' | null
  >(null)

  /**
   * Leave node edit mode and tell the parent which shape was being edited, so it can run
   * `revertPathConversionIfUnedited` for it.
   */
  const exitNodeEditMode = useCallback(
    (exitingShapeId: string | undefined) => {
      setNodeEditShapeId(null)
      setNodeEditShapeData(null)
      onNodeEditModeChange?.(false, exitingShapeId)
    },
    [onNodeEditModeChange]
  )

  return {
    nodeEditShapeId,
    setNodeEditShapeId,
    nodeEditShapeData,
    setNodeEditShapeData,
    nodeEditShapeIdRef,
    nodeEditPreDragShapeRef,
    nodeEditOriginalShapeRef,
    nodeEditToolbarRef,
    nodeEditUndoFloorRef,
    isNodeEditMode,
    isPenEditMode,
    penEditHoverKind,
    setPenEditHoverKind,
    exitNodeEditMode,
  }
}
